// src/pages/AboutPage.js
import React from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";

export default function AboutPage() {
  const navigate = useNavigate();

  const navButtonStyle = {
    background: "transparent",
    color: "rgba(255,255,255,0.85)",
    border: "none",
    fontSize: "1rem",
    cursor: "pointer",
    fontWeight: "500",
    textDecoration: "none",
    transition: "all 0.2s ease",
  };

  const handleHover = (e, entering) => {
    e.target.style.color = entering ? "#ffffff" : "rgba(255,255,255,0.85)";
    e.target.style.transform = entering ? "translateY(-1px)" : "translateY(0)";
  };

  return (
    <>
      <Helmet>
        <title>About PBIS Portal | PBIS Software for Schools</title>
        <meta
          name="description"
          content="Learn about PBIS Portal and how it helps schools track positive behavior and reward students."
        />
        <link rel="canonical" href="https://pbisportal.com/about" />
      </Helmet>
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" }}>
      
      {/* HEADER */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 2rem",
          backgroundColor: "#1e3a8a",
          color: "white",
          position: "sticky",
          top: 0,
          zIndex: 1000,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
        }}
      >
        <Link
          to="/"
          style={{
            fontSize: "1.8rem",
            fontWeight: "700",
            cursor: "pointer",
            color: "white",
            textDecoration: "none",
          }}
        >
          🎯 PBIS Portal
        </Link>

        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <Link
            to="/about"
            style={navButtonStyle}
            onMouseEnter={(e) => handleHover(e, true)}
            onMouseLeave={(e) => handleHover(e, false)}
          >
            About
          </Link>

          <Link
            to="/features"
            style={navButtonStyle}
            onMouseEnter={(e) => handleHover(e, true)}
            onMouseLeave={(e) => handleHover(e, false)}
          >
            Features
          </Link>

          <Link
            to="/what-is-pbis"
            style={navButtonStyle}
            onMouseEnter={(e) => handleHover(e, true)}
            onMouseLeave={(e) => handleHover(e, false)}
          >
            What is PBIS
          </Link>
          <Link
  to="/contact"
  style={navButtonStyle}
  onMouseEnter={(e) => handleHover(e, true)}
  onMouseLeave={(e) => handleHover(e, false)}
>
  Contact
</Link>

          {/* Divider */}
          <div
            style={{
              width: "1px",
              height: "20px",
              background: "rgba(255,255,255,0.3)",
            }}
          />

          <Link
            to="/login"
            style={{
              backgroundColor: "#2563eb",
              color: "#fff",
              border: "none",
              padding: "0.65rem 1.2rem",
              fontSize: "1rem",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              marginLeft: "0.5rem",
            }}
          >
            Log In
          </Link>
        </div>
      </header>

      {/* HERO */}
      <div
        style={{
          textAlign: "center",
          padding: "4rem 2rem",
          background: "linear-gradient(135deg, #e0f2fe 0%, #ecfeff 100%)",
        }}
      >
        <h1
          style={{
            fontSize: "2.8rem",
            fontWeight: "800",
            marginBottom: "1rem",
            color: "#1e293b",
          }}
        >
          What is PBIS Portal
        </h1>

        <p
          style={{
            fontSize: "1.2rem",
            maxWidth: "750px",
            margin: "0 auto",
            color: "#475569",
            lineHeight: "1.7",
          }}
        >
          PBIS Portal is a positive behavior recognition and student reward system designed to help schools promote positive culture and celebrate student success.
        </p>
      </div>

      {/* CONTENT */}
      <div
        style={{
          maxWidth: "900px",
          margin: "3rem auto",
          padding: "0 2rem",
        }}
      >
        <div style={cardStyle}>
          <h2 style={sectionTitle}>🎯 Why PBIS Portal Exists</h2>
          <p style={paragraph}>
            PBIS Portal was created to solve a real problem in schools — tracking
            and rewarding positive student behavior often takes too much time and too
            many disconnected tools. This platform brings everything into one
            simple system so teachers and staff can focus more on students and
            less on paperwork.
          </p>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitle}>⚙️ What It Does</h2>
          <ul style={listStyle}>
            <li>Track PBIS tickets in real-time</li>
            <li>Reward positive actions with a digital ticket system</li>
            <li>Run a customizable school store</li>
            <li>Manage students, staff, and advisories</li>
            <li>View data dashboards and behavior trends</li>
          </ul>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitle}>👨‍💻 Built for Real Schools</h2>
          <p style={paragraph}>
            PBIS Portal was independently developed by an educator to meet the
            real needs of schools using PBIS systems every day. It is actively
            used in live school environments and continues to evolve based on
            feedback from teachers and staff.
          </p>
        </div>

        <div style={cardStyle}>
          <h2 style={sectionTitle}>🚀 Our Goal</h2>
          <p style={paragraph}>
            The goal of PBIS Portal is simple: make it easier for schools to
            recognize positive behavior, motivate students, and build a strong
            school culture without adding extra workload for staff.
          </p>
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <button
            onClick={() => navigate("/contact")}
            style={{
              backgroundColor: "#2563eb",
              color: "white",
              padding: "0.75rem 1.75rem",
              borderRadius: "8px",
              border: "none",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
            }}
          >
            Contact Us
          </button>
        </div>
      </div>
      </div>
</>
);
}

// 🎨 Styles
const cardStyle = {
  backgroundColor: "white",
  padding: "1.75rem",
  borderRadius: "14px",
  marginBottom: "1.5rem",
  boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
};

const sectionTitle = {
  fontSize: "1.4rem",
  fontWeight: "700",
  marginBottom: "0.75rem",
  color: "#1e293b",
};

const paragraph = {
  fontSize: "1.05rem",
  color: "#475569",
  lineHeight: "1.7",
};

const listStyle = {
  paddingLeft: "1.25rem",
  color: "#475569",
  lineHeight: "1.8",
};
